import type { BudgetPeriod, Commitment, ForecastPeriodType } from '@finapp/types';
import { useCallback, useEffect, useState } from 'react';
import { budgetPeriodsApi } from '../services/budgetPeriodsApi';
import { commitmentsApi } from '../services/commitmentsApi';

interface UseCommitmentsResult {
  commitments: Commitment[];
  // Every budget period that exists for `year` (ANNUAL/H1/H2, whichever were created) -
  // the candidates a new commitment can explicitly be attached to. Deliberately NOT scoped
  // to the current periodType view: you might be looking at the "All" forecast but still
  // want to commit specifically against H2, say.
  availablePeriods: BudgetPeriod[];
  // A reasonable pre-selected default among availablePeriods for the current periodType
  // view - null when there's nothing to attach to yet. The modal shows this plainly and
  // lets the user change it, rather than silently deciding "where it goes".
  defaultBudgetPeriodId: number | null;
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

// Mirrors ForecastService.getCommitmentTotalsByCostpoint's own year/periodType join: 'ALL'
// counts every budget_periods row in the year regardless of its own period_type, 'H1'/'H2'
// only the matching one. Keeps the commitments listed here consistent with the budgeted
// totals the forecast itself displays for the current view.
function matchesPeriodType(period: BudgetPeriod, periodType: ForecastPeriodType): boolean {
  return periodType === 'ALL' || period.period_type === periodType;
}

const useCommitments = (year: number, periodType: ForecastPeriodType): UseCommitmentsResult => {
  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [availablePeriods, setAvailablePeriods] = useState<BudgetPeriod[]>([]);
  const [defaultBudgetPeriodId, setDefaultBudgetPeriodId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const periods = await budgetPeriodsApi.listPeriods();
      const forYear = periods.filter((p) => p.year === year);
      setAvailablePeriods(forYear);

      const matching = forYear.filter((p) => matchesPeriodType(p, periodType));

      // Prefer an ANNUAL period as the default pick for the 'ALL' view (most orgs budget
      // annually); otherwise the single H1/H2 match; otherwise none - the "add commitment"
      // form stays disabled with an explanatory message until a budget period exists.
      const defaultPeriod = matching.find((p) => p.period_type === 'ANNUAL') ?? matching[0] ?? null;
      setDefaultBudgetPeriodId(defaultPeriod?.id ?? null);

      const results = await Promise.all(
        matching.map((p) => commitmentsApi.list({ budgetPeriodId: p.id }))
      );
      setCommitments(results.flat());
    } catch (err) {
      console.error('Failed to load commitments:', err);
      setError('Failed to load commitments. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [year, periodType]);

  useEffect(() => {
    load();
  }, [load]);

  return { commitments, availablePeriods, defaultBudgetPeriodId, isLoading, error, reload: load };
};

export default useCommitments;
